import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';
import { patchArtifact } from '@/services/artifacts/patch-artifact';
import { deleteArtifact } from '@/services/artifacts/delete-artifact';
import { artifactsListQueryKey } from '@/hooks/queries/artifacts/use-list-artifacts';
import { artifactDetailQueryKey } from '@/hooks/queries/artifacts/use-get-artifact';
import type {
  ArtifactDetailResponse,
  ArtifactListItem,
  ArtifactListResponse,
} from '@/types/artifacts';

type CachedArtifactPatch = Pick<
  ArtifactListItem,
  'title' | 'status' | 'updated_at'
>;

function patchArtifactList(
  current: ArtifactListResponse | undefined,
  artifactId: string,
  patch: Partial<CachedArtifactPatch>,
): ArtifactListResponse | undefined {
  if (!current) return current;

  return {
    ...current,
    artifacts: current.artifacts.map((artifact) =>
      artifact.id === artifactId ? { ...artifact, ...patch } : artifact,
    ),
  };
}

export const useRenameArtifact = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      artifactId,
      title,
    }: {
      artifactId: string;
      title: string;
    }) => patchArtifact(orgId!, artifactId, { title }),
    onMutate: async ({ artifactId, title }) => {
      const listQueryKey = artifactsListQueryKey(orgId);
      const detailQueryKey = artifactDetailQueryKey(orgId, artifactId);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: detailQueryKey }),
      ]);

      const listSnapshots = queryClient.getQueriesData<ArtifactListResponse>({
        queryKey: listQueryKey,
      });
      const detailSnapshot =
        queryClient.getQueryData<ArtifactDetailResponse>(detailQueryKey);
      const optimisticPatch = {
        title,
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueriesData<ArtifactListResponse>(
        { queryKey: listQueryKey },
        (current) => patchArtifactList(current, artifactId, optimisticPatch),
      );
      queryClient.setQueryData<ArtifactDetailResponse>(
        detailQueryKey,
        (current) => (current ? { ...current, ...optimisticPatch } : current),
      );

      return { listSnapshots, detailSnapshot, detailQueryKey };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, snapshot] of context?.listSnapshots ?? []) {
        queryClient.setQueryData(queryKey, snapshot);
      }
      if (context?.detailSnapshot) {
        queryClient.setQueryData(
          context.detailQueryKey,
          context.detailSnapshot,
        );
      }
    },
    onSuccess: ({ artifact }, { artifactId }, context) => {
      // Only title/status/updated_at: include_urls=false means the PATCH
      // response omits primary_file_* fields, so spreading the whole
      // artifact here would wipe those out of every cached list row.
      const authoritativePatch = {
        title: artifact.title,
        status: artifact.status,
        updated_at: artifact.updated_at,
      };
      const listQueryKey = artifactsListQueryKey(orgId);

      queryClient.setQueriesData<ArtifactListResponse>(
        { queryKey: listQueryKey },
        (current) => patchArtifactList(current, artifactId, authoritativePatch),
      );
      queryClient.setQueryData<ArtifactDetailResponse>(
        context?.detailQueryKey ?? artifactDetailQueryKey(orgId, artifactId),
        (current) =>
          current ? { ...current, ...authoritativePatch } : current,
      );
    },
    onSettled: () => {
      // The cache above only patches title/status/updated_at in place, it
      // doesn't reorder or refilter — mark list queries stale (without
      // forcing a refetch) so ordering and search-membership reconcile with
      // the server on the next natural fetch.
      queryClient.invalidateQueries({
        queryKey: artifactsListQueryKey(orgId),
        refetchType: 'none',
      });
    },
  });
};

export const useDeleteArtifact = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (artifactId: string) => deleteArtifact(orgId!, artifactId),
    onSuccess: (_, artifactId) => {
      queryClient.invalidateQueries({
        queryKey: artifactsListQueryKey(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: artifactDetailQueryKey(orgId, artifactId),
      });
    },
  });
};
